import { useState } from "react";
import { toast } from "sonner";
import { Copy, Pencil, Users } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const splitNames = (text) => text.split(/[,;\r\n]+/).map((n) => n.trim()).filter(Boolean).slice(0, 60);
const code = (a) => (a || "").split(" · ")[0];

function PaxEditor({ names, onCancel, onSave, i }) {
  const [text, setText] = useState(names.join("\n"));
  const [busy, setBusy] = useState(false);
  const preview = splitNames(text);
  const save = async () => {
    setBusy(true);
    try { await onSave(preview); } catch { /* toast already shown */ } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid={`pax-editor-${i}`}>
      <Textarea rows={5} className="bg-[#0d0f14] border-white/10" value={text} onChange={(e) => setText(e.target.value)} data-testid={`pax-paste-input-${i}`}
        placeholder="Paste all names at once, e.g. John Smith, Jane Doe; Bob Lee" />
      <p className="text-[11px] text-zinc-500" data-testid={`pax-preview-count-${i}`}>Split on commas, semicolons or new lines · {preview.length} name{preview.length === 1 ? "" : "s"}</p>
      <div className="flex justify-end gap-2">
        <button type="button" className="frame-btn !py-2.5 !px-4" onClick={onCancel} data-testid={`pax-cancel-btn-${i}`}>Cancel</button>
        <button type="button" className="frame-btn gold !py-2.5 !px-4" disabled={busy} onClick={save} data-testid={`pax-save-btn-${i}`}>{busy ? "Saving…" : "Save list"}</button>
      </div>
    </div>
  );
}

function PaxLeg({ lg, i, staff, onSave }) {
  const [editing, setEditing] = useState(false);
  const names = lg.passengers || [];
  const copy = () => navigator.clipboard.writeText(names.join("\n")).then(() => toast.success("Passenger list copied")).catch(() => toast.error("Could not copy"));
  return (
    <div data-testid={`pax-leg-${i}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <p className="text-xs text-zinc-400">{code(lg.from_airport)} → {code(lg.to_airport)} · {fmtDate(lg.date)} · {names.length} of {lg.pax || "—"} pax listed</p>
        <div className="flex gap-2">
          {names.length > 0 && <button type="button" className="frame-btn !py-2 !px-3" onClick={copy} data-testid={`pax-copy-btn-${i}`}><Copy size={12} /> Copy list</button>}
          {staff && !editing && <button type="button" className="frame-btn !py-2 !px-3" onClick={() => setEditing(true)} data-testid={`pax-edit-btn-${i}`}><Pencil size={12} /> {names.length ? "Edit" : "Add names"}</button>}
        </div>
      </div>
      {editing ? <PaxEditor names={names} i={i} onCancel={() => setEditing(false)} onSave={(n) => onSave(i, n).then(() => setEditing(false))} /> : names.length ? (
        <ol className="space-y-1.5" data-testid={`pax-names-${i}`}>
          {names.map((n, k) => (
            <li key={k} className="flex gap-3 text-sm text-zinc-100"><span className="font-mono-data text-[11px] text-[#5B7FD1] w-6 pt-0.5">{String(k + 1).padStart(2, "0")}</span>{n}</li>
          ))}
        </ol>
      ) : <p className="text-sm text-zinc-500" data-testid={`pax-empty-${i}`}>No passenger names yet.</p>}
    </div>
  );
}

export function PassengerList({ tripId, legs, staff, onSaved }) {
  const api = useApi();
  const save = async (i, names) => {
    try {
      await api.put(`/trips/${tripId}/legs/${i}/passengers`, { passengers: names });
      toast.success("Passenger list saved");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); throw err; }
  };
  return (
    <section className="lux-card p-5 sm:p-6" data-testid="passenger-list">
      <p className="eyebrow mb-4 flex items-center gap-2"><Users size={13} /> Passenger list</p>
      <Tabs defaultValue="0">
        <TabsList className="flex flex-wrap h-auto justify-start gap-1 bg-white/[0.04] mb-5" data-testid="pax-leg-tabs">
          {legs.map((lg, i) => (
            <TabsTrigger key={i} value={String(i)} className="text-xs" data-testid={`pax-tab-${i}`}>Leg {i + 1} ({(lg.passengers || []).length})</TabsTrigger>
          ))}
        </TabsList>
        {legs.map((lg, i) => <TabsContent key={i} value={String(i)}><PaxLeg lg={lg} i={i} staff={staff} onSave={save} /></TabsContent>)}
      </Tabs>
    </section>
  );
}
